// src/context/ProfileContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';
import { POST_VIEW_COUNT_UPDATED_EVENT } from '../services/postViewTracker';

const ProfileContext = createContext();
const DEBUG = true;

export function ProfileProvider({ children }) {


  const [profilePosts, setProfilePosts] = useState([]);
  const [profileOwnerUsername, setProfileOwnerUsername] = useState(null);
  const [profileLastFetchedAt, setProfileLastFetchedAt] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);



  // Keep cached post author pictures synchronized with profile changes.
  useEffect(() => {
    const onUserUpdated = (event) => {
      const updatedUser = event?.detail?.user;

      if (
        !updatedUser ||
        typeof updatedUser.profilePic !== 'string'
      ) {
        return;
      }

      const updatedId = String(updatedUser._id || '');

      const updatedUsername = String(
        updatedUser.username || ''
      ).trim().toLowerCase();

      if (!updatedId && !updatedUsername) return;

      function matchesAuthor(author) {
        if (!author || typeof author !== 'object') {
          return false;
        }

        const authorId = String(author._id || author.id || '');

        // Prefer IDs when both are available.
        if (updatedId && authorId) {
          return updatedId === authorId;
        }

        const authorUsername = String(
          author.username || ''
        ).trim().toLowerCase();

        return Boolean(
          updatedUsername &&
          authorUsername === updatedUsername
        );
      }

      function patchPost(post) {
        if (!post || typeof post !== 'object') return post;

        let next = post;

        // Populated author data used by post cards.
        if (
          matchesAuthor(post.user) &&
          post.user.profilePic !== updatedUser.profilePic
        ) {
          next = {
            ...next,
            user: {
              ...post.user,
              profilePic: updatedUser.profilePic,
            },
          };
        }

        // Older posts may carry author fields at the top level.
        const topLevelAuthorMatches =
          post.username &&
          String(post.username).trim().toLowerCase() ===
            updatedUsername;

        if (
          topLevelAuthorMatches &&
          post.profilePic !== updatedUser.profilePic
        ) {
          next = {
            ...next,
            profilePic: updatedUser.profilePic,
          };
        }

        // Also patch the original author inside a repost.
        if (
          post.repostOf &&
          typeof post.repostOf === 'object'
        ) {
          const original = patchPost(post.repostOf);

          if (original !== post.repostOf) {
            next = {
              ...next,
              repostOf: original,
            };
          }
        }

        return next;
      }

      setProfilePosts((previous) => {
        let changed = false;

        const next = previous.map((post) => {
          const patched = patchPost(post);

          if (patched !== post) changed = true;

          return patched;
        });

        return changed ? next : previous;
      });

      // Mark the cached profile as stale for its next fetch.
      setProfileLastFetchedAt(0);
    };

    window.addEventListener('user:updated', onUserUpdated);

    return () => {
      window.removeEventListener('user:updated', onUserUpdated);
    };
  }, []);


  // 🔹 Listen for deletes coming from anywhere

  useEffect(() => {
    const onExternalDelete = (e) => {
      const postId = e?.detail?.postId;
      if (!postId) return;

      setProfilePosts((prev) => {
        const updated = prev.filter((p) => String(p._id) !== String(postId));
        if (DEBUG) console.log('🧹 [ProfileContext] Removed via event:', postId);
        return updated;
      });
    };

    window.addEventListener('post:deleted', onExternalDelete);
    return () => window.removeEventListener('post:deleted', onExternalDelete);
  }, []);


  // ✅ NEW: Listen for edits/updates and patch in-place + persist
  useEffect(() => {
    const onExternalUpdate = (e) => {
      const updatedPost = e?.detail?.post;
      if (!updatedPost?._id) return;

      setProfilePosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;

        let found = false;
        const next = prev.map((p) => {
          if (String(p._id) === String(updatedPost._id)) {
            found = true;
            return {
              ...p,
              ...updatedPost,
              user: updatedPost.user || p.user,
            };
          }
          return p;
        });

        if (found) {
          if (DEBUG) console.log('✏️ [ProfileContext] Updated via event:', updatedPost._id);
          return next;
        }

        return prev;
      });
    };

    window.addEventListener('post:updated', onExternalUpdate);
    return () => window.removeEventListener('post:updated', onExternalUpdate);
  }, []);


  // Keep authoritative view counts synchronized in Profile state
  useEffect(() => {
    const handleViewCountUpdate = (event) => {
      const postId = String(event?.detail?.postId || '');
      const nextViewCount = Number(event?.detail?.viewCount);

      if (!postId || !Number.isFinite(nextViewCount)) return;

      const safeViewCount = Math.max(0, nextViewCount);

      setProfilePosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return prev;
        }

        let changed = false;

        const nextPosts = prev.map((post) => {
          if (!post) return post;

          let nextPost = post;

          const currentPostId = String(post._id || post.id || '');

          if (
            currentPostId === postId &&
            Number(post.viewCount || 0) !== safeViewCount
          ) {
            nextPost = {
              ...nextPost,
              viewCount: safeViewCount,
            };

            changed = true;
          }

          const original = nextPost.repostOf;

          if (original && typeof original === 'object') {
            const originalId = String(
              original._id || original.id || ''
            );

            if (
              originalId === postId &&
              Number(original.viewCount || 0) !== safeViewCount
            ) {
              nextPost = {
                ...nextPost,
                repostOf: {
                  ...original,
                  viewCount: safeViewCount,
                },
              };

              changed = true;
            }
          }

          return nextPost;
        });

        return changed ? nextPosts : prev;
      });
    };

    window.addEventListener(
      POST_VIEW_COUNT_UPDATED_EVENT,
      handleViewCountUpdate
    );

    return () => {
      window.removeEventListener(
        POST_VIEW_COUNT_UPDATED_EVENT,
        handleViewCountUpdate
      );
    };
  }, []);



  const clearProfileState = useCallback(() => {
    if (DEBUG) console.log('♻️ [ProfileContext] Clearing state');

    setProfilePosts([]);
    setProfileOwnerUsername(null);
    setProfileLastFetchedAt(0);
    setPage(1);
    setHasMore(true);
    setHasLoaded(false);
  }, []);


  // 🔄 Enrich post before saving
  const addEnrichedPost = async (newPost, username) => {
    try {
      const res = await api.get(`/posts/interactions/${newPost._id}`);
      const { likes = [], comments = [], tips = [], saves = [] } = res.data;

      if (DEBUG) {
        console.log('📬 [ProfileContext] Interactions for', newPost._id, {
          likes,
          comments,
          tips,
          saves,
        });
      }

      // ✅ Build mentionMap from mentions array (lowercase keys)
      const mentionMap = {};
      if (Array.isArray(newPost.mentions)) {
        newPost.mentions.forEach((m) => {
          const key = `@${m.username.toLowerCase()}`;
          mentionMap[key] = {
            username: m.username,
            type: m.type,
          };
        });
      }

      const enriched = {
        ...newPost,
        viewCount: Math.max(0, Number(newPost.viewCount) || 0),
        likes,

        comments,
        tipCount: tips.length,
        likesCount: likes.length,
        commentsCount: comments.length,
        userHasLiked: likes.some((u) => u.username === username),
        userHasSaved: Array.isArray(saves) ? saves.includes(username) : false,
        timestamp: newPost.createdAt,
        type: 'post',
        mentionMap, // ✅ Attach mentionMap to post
        user: newPost.user || {
          name: newPost.name,
          username: newPost.username,
          profilePic: newPost.profilePic,
          userType: newPost.userType,
          isMerchant: newPost.isMerchant,
          location: newPost.location,
        },
      };

      addNewPost(enriched);
      if (DEBUG) console.log('✅ [ProfileContext] Enriched post saved:', enriched._id);
    } catch (err) {
      console.warn('⚠️ [ProfileContext] Failed to enrich post:', newPost._id, err);
      addNewPost(newPost);
    }
  };

  // ➕ Add to top

  const addNewPost = (newPost) => {
    const normalizedPost = {
      ...newPost,
      viewCount: Math.max(0, Number(newPost?.viewCount) || 0),
    };

    setProfilePosts((prev) => {
      const alreadyExists = prev.some(
        (p) => String(p._id) === String(normalizedPost._id)
      );

      if (alreadyExists) {
        if (DEBUG) {
          console.log(
            '⚠️ [ProfileContext] Duplicate post skipped:',
            normalizedPost._id
          );
        }

        return prev;
      }

      const updated = [normalizedPost, ...prev];

      if (DEBUG) {
        console.log(
          '✅ [ProfileContext] Added new post to state:',
          normalizedPost._id
        );
      }

      return updated;
    });
  };



  // 📥 Pagination support
  const loadMorePosts = (newPosts) => {
    setProfilePosts((prev) => {
      const combined = [...prev];

      newPosts.forEach((post) => {
        const normalizedPost = {
          ...post,
          viewCount: Math.max(0, Number(post?.viewCount) || 0),
        };

        if (
          !combined.some(
            (p) => String(p._id) === String(normalizedPost._id)
          )
        ) {
          combined.push(normalizedPost);

        } else {
          if (DEBUG) console.log('⚠️ [ProfileContext] Skipped duplicate in loadMore:', post._id);
        }
      });
      return combined;
    });
    setHasMore(newPosts.length === 10);
    setPage((prev) => prev + 1);
  };


  return (
    <ProfileContext.Provider
      value={{

        profilePosts,
        setProfilePosts,
        profileOwnerUsername,
        setProfileOwnerUsername,
        profileLastFetchedAt,
        setProfileLastFetchedAt,
        page,

        setPage,
        hasMore,
        setHasMore,
        hasLoaded,
        setHasLoaded,
        addNewPost,
        addEnrichedPost,
        loadMorePosts,
        clearProfileState,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfileContext() {
  return useContext(ProfileContext);
}
